import Vue from 'vue'
import { configureScope } from '@sentry/browser'
import authService from '@/api/auth'
import commonService from '@/api/common'
import { fileToFrontend } from '@/utils/objUtil'
import { isProd, ls, getFingerprint } from '@/utils/index'
import { getToken, setToken, removeToken } from '@/utils/auth'
import watermark from '@/utils/watermark'
import { router, filterAuthRoutes, personAllRoutes, isAIPublish } from '@/router'
import aiPublishRoutes from '@/router/ai_publish'
import { cloneDeep } from 'lodash-es'
// const userInfo = ls.getItem('userInfo', {})

export const CACHE_USERNAME = 'username'
const username = ls.getItem(CACHE_USERNAME, '')

function customAddRoute(route, parentName) {
  const oldRoutes = router.getRoutes()
  // 存在的路由不添加
  if (oldRoutes.find(oldRoute => oldRoute.path === route.path)) return
  if (parentName) {
    router.addRoute(parentName, route)
  } else {
    router.addRoute(route)
  }
}

const user = {
  state: {
    token: getToken(),
    userInfo: {},
    roles: [],
    perms: [],
    company: null, // 厂牌身份的信息
    editorObj: null, // 编务身份的信息
    singer: null, // AI发行身份的信息
    originCompanyList: [], // 用户关联公司的原始版权公司列表,从后端获取
    relateCompanyList: [],
    agentCompanyList: [],
    copyrightCompanyList: [],
    userIdentifyList: [], // 第三方-个人身份证图片列表
    showLogin: false, // 展示登录弹窗
    redirectUrl: '',
    noticeNum: 0,
    channelId: '',
    privateInfo: null,
    hasLogin: false, // 是否已经登录
    unionLoginCookie: '',
    markdownMap: {},
    markdownVersionMap: {}
  },

  getters: {
    token(state) {
      return state.token
    },
    hasTrackBasicAuth(state) {
      return state.perms.find(perm => perm.key === 'track/modify_basic_info' && perm.checkType === 1)
    },
    hasAlbumBasicAuth(state) {
      return state.perms.find(perm => perm.key === 'album/modify_basic_info' && perm.checkType === 1)
    },
    hasAlbumCopyrightAuth(state) {
      return state.perms.find(perm => perm.key === 'album/modify_copyright_info' && perm.checkType === 1)
    },
    hasSingerBasicAuth(state) {
      return state.perms.find(perm => perm.key === 'singer/batch_update' && perm.checkType === 1)
    },

    roles(state) {
      return state.roles
    },
    isAdmin(state) {
      return state.roles.includes('system_admin')
    },
    isCompany(state) {
      return state.roles.includes('厂牌用户组')
    },
    isEditor(state) {
      return state.roles.some(role => ['编务用户组', '侵权编务组', '认领编务组'].includes(role))
    },
    isBussiness(state) {
      return state.roles.includes('商务用户组')
    },
    isLegal(state) {
      return state.roles.includes('法务用户组')
    },
    isOther(state) {
      return state.roles.includes('其他用户组')
    },
    isFinance(state) {
      return state.roles.includes('财经用户组')
    },
    isSubsidiary(state) {
      return state.roles.includes('附属功能用户组')
    },
    isCpTrackOnline(state) {
      return state.roles.includes('版权歌曲上架组')
    },
    isCompanyOnline(state) {
      return state.roles.includes('厂牌上架组')
    },
    isThirdParty(state) {
      return state.roles.includes('第三方组')
    },
    isInfringement(state) {
      return state.roles.includes('侵权编务组')
    },
    isClaim(state) {
      return state.roles.includes('认领编务组')
    },
    isOnlyInfringement(state, getters) {
      return getters.isInfringement && !getters.isClaim && !getters.isEditor && !getters.isFinance && !getters.isAdmin
    },
    isOnlyClaim(state, getters) {
      return getters.isClaim && !getters.isInfringement && !getters.isEditor && !getters.isFinance && !getters.isAdmin
    },
    isAIPublish(state) {
      return state.roles.includes('AI发行组')
    },

    userInfo(state) {
      return state.userInfo
    },
    channelId(state) {
      return state.channelId
    },
    showLogin(state) {
      return state.showLogin
    },
    hasPermission(state) {
      return state.userInfo.level > 10
    },
    noticeNum(state) {
      return state.noticeNum
    },
    company(state) {
      return state.company
    },
    editorObj(state) {
      return state.editorObj
    },
    singer(state) {
      return state.singer
    },
    originCompanyList(state) {
      return state.originCompanyList
    },
    relateCompanyList(state) {
      return state.relateCompanyList
    },
    agentCompanyList(state) {
      return state.agentCompanyList
    },
    copyrightCompanyList(state) {
      return state.copyrightCompanyList
    },
    userIdentifyList(state) {
      return state.userIdentifyList
    },
    hasShowAllPerm(state, getters) {
      if (getters.isAdmin) {
        return true
      }
      if (state.editorObj) {
        return state.editorObj.isShowAll === 1
      }
      if (state.company) {
        return state.company.isShowAll === 1
      }
      return false
    },
    showDataManage(state, getters) {
      if (getters.isAdmin) {
        return true
      }
      if (getters.isSubsidiary) {
        return false
      }
      if (state.editorObj) {
        return state.editorObj.status === 1 && state.editorObj.isShowAll !== 2
      }
      if (state.company) {
        return state.company.status === 1 && state.company.isShowAll !== 2
      }
      return false
    },
    ixYxCompanyUser(state, getters) {
      if (getters.isAdmin) {
        return true
      }
      if (state.company && [3, 4, 5].includes(state.company.type)) {
        return true
      }
      return false
    },
    // 是否外部用户
    isExternalUser(state) {
      return [7, 8].includes(+state.userInfo.roleType)
    },
    // 获取基础信息
    getBasicInfo(state) {
      if (state.privateInfo) return state.privateInfo
      else if (state.company) {
        return {
          ...state.company,
          name: state.company.companyName,
          licenseList: state.company.companyLicenseList
        }
      } else {
        return {
          name: state.userInfo.username
        }
      }
    },
    // 是否已经登录
    hasLogin(state) {
      return !!state.hasLogin
    },
    notRegister(state) {
      return !state.userInfo.level || state.userInfo.level === 1
    },
    markdownMap(state) {
      return state.markdownMap
    },
    markdownVersionMap(state) {
      return state.markdownVersionMap
    },
    unionLoginCookie(state) {
      return state.unionLoginCookie || ls.getItem('unionLoginCookie')
    }
  },

  mutations: {
    SET_TOKEN: (state, token) => {
      state.token = token
    },
    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo
    },
    SET_PERMS: (state, perms) => {
      state.perms = perms
    },
    SET_ROLES: (state, roles) => {
      state.roles = roles
    },
    SET_COMPANY: (state, company) => {
      state.company = company
    },
    SET_EDITOR: (state, editorObj) => {
      state.editorObj = editorObj
    },
    SET_SINGER: (state, singer) => {
      state.singer = singer
    },
    SET_CHANNEL_ID(state, channelId) {
      state.channelId = channelId
    },
    SET_ORIGIN_COMPANY: (state, originCompanyList) => {
      state.originCompanyList = originCompanyList
    },
    SET_RELATE_COMPANY(state, relateCompanyList) {
      state.relateCompanyList = relateCompanyList
    },
    SET_AGENT_COMPANY(state, agentCompanyList) {
      state.agentCompanyList = agentCompanyList
    },
    SET_COPYRIGHT_COMPANY(state, copyrightCompanyList) {
      state.copyrightCompanyList = copyrightCompanyList
    },
    SET_USER_IDENTIFY_COMPANY(state, userIdentifyList) {
      state.userIdentifyList = userIdentifyList
    },
    SET_WATERMARK_PIC(state, picBase64) {
      state.watermark = picBase64
    },
    login: (state, showLogin) => {
      state.showLogin = showLogin
    },
    redirectUrl: (state, url) => {
      state.redirectUrl = url
    },
    SET_PRIVATE_INFO: (state, privateInfo) => {
      state.privateInfo = privateInfo
    },
    SET_HAS_LOGIN: (state, hasLogin) => {
      state.hasLogin = hasLogin
    },
    SET_UNION_LOGIN_COOKIE: (state, unionLoginCookie) => {
      ls.setItem('unionLoginCookie', unionLoginCookie)
      state.unionLoginCookie = unionLoginCookie
    }
  },

  actions: {
    // QQ登录
    Login({ commit }, data) {
      return authService.login(data).then(response => {
        const data = response
        setToken(data.id)
        commit('SET_TOKEN', data.id)
        return response
      }).catch(error => {
        throw error
      })
    },

    // 新QQ登录
    LoginNew({ commit }, data) {
      return authService.loginByCode(data).then(response => {
        const data = response
        setToken(data.id)
        commit('SET_TOKEN', data.id)
        commit('SET_UNION_LOGIN_COOKIE', data.cookie)
        return response
      }).catch(error => {
        throw error
      })
    },

    // 邮箱登录
    LoginEmail({ commit }, data) {
      return authService.loginEmail(data).then(response => {
        const data = response
        setToken(data.id)
        commit('SET_TOKEN', data.id)
        return response
      }).catch(error => {
        throw error
      })
    },

    // 发送验证码
    SendCode({ dispatch }, phone) {
      return getFingerprint().then(visitorId => {
        return authService.sendCode({
          phoneNum: phone,
          deviceinfo: `udid=${visitorId}`
        }).then((data) => {
          if (data.url) {
            return new Promise((resolve, reject) => {
              // eslint-disable-next-line no-new, no-undef
              new SafetyCaptcha({
                useMiniSize: true,
                verifyUrl: data.url,
                callback() {
                  resolve(dispatch('SendCode', phone))
                },
                initiativeCloseCb() { // 主动关闭图形验证码弹窗，mock一个后台不会返回的错误码
                  resolve({ code: 99999, message: '关闭图形验证码' })
                }
              })
            })
          }
          return data
        })
      })
    },

    // 验证码
    VerifyCode({ commit }, data) {
      return authService.verifyCode(data)
    },

    // 绑定手机号
    BindPhone({ commit, state }, data) {
      return authService.bindPhoneNum({
        ...data,
        cookie: state.unionLoginCookie || ls.getItem('unionLoginCookie')
      })
    },

    // 手机号登录
    LoginPhone({ commit }, data) {
      return authService.loginPhone(data).then(response => {
        const data = response
        setToken(data.id)
        commit('SET_TOKEN', data.id)
        commit('SET_UNION_LOGIN_COOKIE', data.cookie)
        return response
      }).catch(error => {
        throw error
      })
    },

    // 获取用户信息
    GetInfo({ commit, state, getters }) {
      return authService.getUserInfo().then(response => {
        const {
          user, company, editor, singer, relateCompanyList, companyChannelIdMd5,
          agentCompanyList, copyrightCompanyList, userIdentifyList
        } = response
        const currentName = user.username

        commit('SET_USER_INFO', {
          level: user.level,
          username: currentName,
          email: user.email,
          phoneNum: user.phoneNum,
          figureurl: user.figureurl,
          roleType: user.roleType,
          openId: user.openId,
          hasBindPlatform: user.hasBindPlatform
        })
        commit('SET_PERMS', user.permList)
        commit('SET_ROLES', user.groupList)
        commit('SET_CHANNEL_ID', companyChannelIdMd5)
        commit('SET_RELATE_COMPANY', relateCompanyList)
        commit('SET_AGENT_COMPANY', agentCompanyList)
        commit('SET_COPYRIGHT_COMPANY', copyrightCompanyList)
        commit('SET_USER_IDENTIFY_COMPANY', fileToFrontend(userIdentifyList, 'fileUrl'))
        commit('SET_HAS_LOGIN', true)

        if (company) {
          company.tempId = `${Math.random()}`
          company.companyLicenseList = fileToFrontend(company.companyLicenseList)
          company.companyPhotoList = fileToFrontend(company.companyPhotoList)
          company.productionList = fileToFrontend(company.productionList, 'fileUrl')
          commit('SET_COMPANY', company)
        }
        if (editor) {
          commit('SET_EDITOR', editor)
        }
        if (singer) {
          commit('SET_SINGER', singer)
        }

        // base64 格式的图片字符串
        const pic = watermark(currentName, { font: '24px Arial', alpha: 1 })
        commit('SET_WATERMARK_PIC', pic)

        if (isProd()) {
          // sentry 记录用户名
          configureScope((scope) => {
            scope.setUser({ username: currentName, openId: user.openId })
          })

          // 上报 SDK 设置用户唯一 ID
          if (username !== currentName) {
            ls.setItem(CACHE_USERNAME, currentName)
            Vue.$aegis.setConfig({
              uin: currentName
            })
          }
        }

        // 根据是否AI平台加载路由，管理员加载所有路由
        let dynamicRoutes = isAIPublish ? aiPublishRoutes() : personAllRoutes
        if (getters.isAdmin) {
          dynamicRoutes = personAllRoutes.concat(aiPublishRoutes())
        }
        const routes = filterAuthRoutes(dynamicRoutes)
        console.log('filter routes => ', cloneDeep(routes))
        routes.forEach(route => customAddRoute(route, 'default'))
        customAddRoute({
          path: '*',
          isShow() {
            return false
          },
          redirect: '/404',
          hidden: true
        })

        console.log('all routes => ', router.getRoutes())

        return response
      }).catch(error => {
        return Promise.reject(error)
      })
    },

    // 登出
    LogOut({ commit, state }) {
      return new Promise((resolve, reject) => {
        Vue.$QC.Login.signOut()
        authService.logout(state.token).then(() => {
          console.log('logout resolve')
          resolve()
        }).catch(error => {
          console.log('logout error')

          reject(error)
        }).finally(_ => {
          commit('SET_TOKEN', '')
          commit('SET_UNION_LOGIN_COOKIE', '')
          commit('SET_USER_INFO', {})
          commit('SET_PERMS', [])
          commit('SET_ROLES', [])
          commit('SET_COMPANY', null)
          commit('SET_EDITOR', null)
          commit('SET_CHANNEL_ID', '')
          commit('SET_RELATE_COMPANY', [])
          commit('SET_AGENT_COMPANY', [])
          commit('SET_COPYRIGHT_COMPANY', [])
          commit('SET_WATERMARK_PIC', '')
          commit('SET_HAS_LOGIN', false)
          removeToken()
          ls.removeItem('unionLoginCookie')
        })
      })
    },

    GetNotice({ commit, state }) {
      commonService.findNotice().then(resp => {
        state.noticeNum = resp.unReadTotal
      }).catch(_ => {
        //
      })
    },

    // 进入用户首页
    EnterHome() {
      router.push({ name: 'personHome' })
    },

    // 进入AI用户首页
    EnterAIHome() {
      router.push({ name: 'aiPersonHome' })
    },

    // 重新获取用户信息
    RefreshUserInfo({ commit }) {
      commit('SET_HAS_LOGIN', false)
    },

    GetMarkdown({ state }) {
      return commonService.findMarkdown().then(resp => {
        state.markdownMap = resp.list.reduce((acc, cur) => {
          acc[cur.uniqueKey] = cur.content
          return acc
        }, {})
        state.markdownVersionMap = resp.list.reduce((acc, cur) => {
          acc[cur.uniqueKey] = cur.version
          return acc
        }, {})
      }).catch(_ => {
        //
      })
    }
  }
}

export default user
